import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../lib/api';

/**
 * Gemeinsames Bedienkonzept der Vorlagen-Seiten (T-0179): oben wählt man, wofür
 * gepflegt wird — global oder ein Standort. Eine Standort-Vorlage entsteht neben
 * der globalen, nicht an ihrer Stelle.
 */

export interface StandortOption {
  id: number;
  name: string;
}

/** Was im Dialog bearbeitet wird: die globale, die eigene des Standorts oder eine neue für ihn. */
export type VorlagenEbene = 'global' | 'standort' | 'standort_neu';

export const useStandortOptionen = () => {
  const [standorte, setStandorte] = useState<StandortOption[]>([]);
  useEffect(() => {
    api
      .get<StandortOption[]>('/api/standorte')
      .then((data) => setStandorte(Array.isArray(data) ? data : []))
      .catch(() => setStandorte([]));
  }, []);
  return standorte;
};

/**
 * Für Vorlagen mit Schlüssel (SMS, Historie): die eigenen Vorlagen des gewählten
 * Standorts, je Schlüssel. Ohne Standort (global) leer.
 */
export const useStandortVorlagen = <T extends { template_key: string }>(
  geltung: number | null,
  laden: (standortId: number) => Promise<T[]>,
) => {
  const [vorlagen, setVorlagen] = useState<Record<string, T>>({});
  useEffect(() => {
    if (geltung === null) {
      setVorlagen({});
      return;
    }
    let aktiv = true;
    laden(geltung)
      .then((liste) => {
        if (aktiv) setVorlagen(Object.fromEntries((liste || []).map((t) => [t.template_key, t])));
      })
      .catch((e: unknown) => {
        if (aktiv) toast.error(e instanceof Error ? e.message : 'Standort-Vorlagen konnten nicht geladen werden');
      });
    return () => {
      aktiv = false;
    };
    // `laden` ist eine stabile API-Funktion; neu geladen wird nur beim Wechsel des Standorts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geltung]);

  const merken = (vorlage: T) => setVorlagen((prev) => ({ ...prev, [vorlage.template_key]: vorlage }));
  const entfernen = (key: string) =>
    setVorlagen((prev) => {
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
  return { vorlagen, merken, entfernen };
};

export const GeltungAuswahl = ({
  standorte,
  geltung,
  onChange,
}: {
  standorte: StandortOption[];
  geltung: number | null;
  onChange: (geltung: number | null) => void;
}) => (
  <div className="md:w-72">
    <Select value={geltung === null ? 'global' : String(geltung)} onValueChange={(wert) => onChange(wert === 'global' ? null : Number(wert))}>
      <SelectTrigger size="md" aria-label="Vorlagen für">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="global">Global (alle Standorte)</SelectItem>
        {standorte.map((s) => (
          <SelectItem key={s.id} value={String(s.id)}>
            Standort {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/** Hinweis oben im Dialog: was gerade bearbeitet wird und wo es gilt. */
export const GeltungHinweis = ({
  geltung,
  geltungName,
  ebene,
  onEigeneAnlegen,
  globalHinweis,
}: {
  geltung: number | null;
  geltungName: string;
  ebene: VorlagenEbene;
  onEigeneAnlegen: () => void;
  /** Ersetzt den Satz über die globale Vorlage, wo „gilt dort“ nicht stimmt (PDF: mehrere Vorlagen). */
  globalHinweis?: ReactNode;
}) => (
  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
    {geltung === null ? (
      <p>Globale Vorlage — gilt an allen Standorten ohne eigene Vorlage.</p>
    ) : ebene === 'standort' ? (
      <p>
        Eigene Vorlage von <strong>{geltungName}</strong> — sie gilt dort vor der globalen.
      </p>
    ) : ebene === 'standort_neu' ? (
      <p>
        Neue Vorlage nur für <strong>{geltungName}</strong>. Sie entsteht beim Speichern; die globale bleibt
        unverändert.
      </p>
    ) : (
      <div className="space-y-2">
        {globalHinweis ?? (
          <p>
            In <strong>{geltungName}</strong> gilt die globale Vorlage. Was du hier speicherst, ändert sie für alle
            Standorte ohne eigene Vorlage.
          </p>
        )}
        <Button type="button" size="sm" variant="outline" onClick={onEigeneAnlegen}>
          Eigene Vorlage für {geltungName} anlegen
        </Button>
      </div>
    )}
  </div>
);
